import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import type { RootState } from "../store";
import LoadingSpinner from "../components/LoadingSpinner";
import { useUserAccess } from "../hooks/useUserAccess";

interface RequireAdminAccessProps {
  children: React.ReactNode;
  requireSuperAdmin?: boolean; // Optional flag to require super admin (level 2)
}

/**
 * RequireAdminAccess - Route guard component for admin-protected routes
 *
 * Access levels:
 * - Level 1 (Admin): Can access all routes except Admin Panel
 * - Level 2 (Super Admin): Can access all routes including Admin Panel
 *
 * @param requireSuperAdmin - If true, only super admins (level 2) can access
 *                            If false, admins (level 1) and super admins (level 2) can access
 */
const RequireAdminAccess: React.FC<RequireAdminAccessProps> = ({
  children,
  requireSuperAdmin = false,
}) => {
  const navigate = useNavigate();
  const [isChecking, setIsChecking] = useState(true);
  const [isAuthorized, setIsAuthorized] = useState(false);

  const agentsLoading = useSelector((state: RootState) => state.agents.loading);
  const { level, isAdmin, isSuperAdmin } = useUserAccess();

  useEffect(() => {
    if (!agentsLoading) {
      // Check authorization based on access level
      let authorized = false;

      if (requireSuperAdmin) {
        // Route requires super admin (level 2)
        authorized = isSuperAdmin;
      } else {
        // Route requires admin or super admin (level 1 or 2)
        authorized = isAdmin || isSuperAdmin;
      }

      if (authorized) {
        setIsAuthorized(true);
        setIsChecking(false);
      } else {
        // Not authorized - redirect to landing page
        navigate("/landing", { replace: true });
      }
    }
  }, [agentsLoading, level, isAdmin, isSuperAdmin, requireSuperAdmin, navigate]);

  // Show loading while checking
  if (agentsLoading || isChecking) {
    return <LoadingSpinner fullscreen label="Verifying access..." />;
  }

  // Only render children if authorized
  if (!isAuthorized) {
    return null;
  }

  return <>{children}</>;
};

export default RequireAdminAccess;
